import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {
    AbsorptionRun, CostEvent, CostingSettings, CostingSettingsRequest, OrderCostDetail, OrderCostPage, PendingShipment,
    ProductionOutput, ProductionOutputRequest, SuggestedAccountsResult,
} from '../interfaces/costing.interface';

const E = ENDPOINTS.COSTING;
type EventBody = { date?: string | null; notes?: string | null };

export const CostingService = {
    getSettings: async (institutionId: string): Promise<CostingSettings> => (await axios.get(E.SETTINGS(institutionId))).data,
    /** Whether costing is on; readable by anyone who can view buyer orders (drives the order picker). */
    isEnabled: async (institutionId: string): Promise<boolean> => (await axios.get(E.ENABLED(institutionId))).data === true,
    /** Server-side order search for the picker: open orders matching the typed text. */
    pickOrders: async (institutionId: string, q: string, openOnly = true): Promise<{ id: string; orderNumber: string; buyerPoNumber?: string | null; status: string }[]> =>
        (await axios.get(ENDPOINTS.BUYER_ORDER.PICK(institutionId), { params: { q, size: 30, openOnly } })).data,
    saveSettings: async (institutionId: string, req: CostingSettingsRequest): Promise<CostingSettings> =>
        (await axios.put(E.SETTINGS(institutionId), req)).data,
    createSuggestedAccounts: async (institutionId: string): Promise<SuggestedAccountsResult> =>
        (await axios.post(E.SUGGESTED_ACCOUNTS(institutionId))).data,

    listOutputs: async (institutionId: string, params: { buyerOrderId?: string; from?: string; to?: string }): Promise<ProductionOutput[]> =>
        (await axios.get(E.OUTPUTS(institutionId), { params })).data,
    createOutput: async (institutionId: string, req: ProductionOutputRequest): Promise<ProductionOutput> =>
        (await axios.post(E.OUTPUTS(institutionId), req)).data,
    updateOutput: async (id: string, req: ProductionOutputRequest): Promise<ProductionOutput> => (await axios.put(E.OUTPUT(id), req)).data,
    deleteOutput: async (id: string): Promise<void> => { await axios.delete(E.OUTPUT(id)); },

    listRuns: async (institutionId: string): Promise<AbsorptionRun[]> => (await axios.get(E.RUNS(institutionId))).data,
    previewRun: async (institutionId: string, period: string): Promise<AbsorptionRun> =>
        (await axios.post(E.RUN_PREVIEW(institutionId), { period })).data,
    getRun: async (id: string): Promise<AbsorptionRun> => (await axios.get(E.RUN(id))).data,
    postRun: async (id: string): Promise<AbsorptionRun> => (await axios.post(E.RUN_POST(id))).data,
    reverseRun: async (id: string): Promise<AbsorptionRun> => (await axios.post(E.RUN_REVERSE(id))).data,

    listOrders: async (institutionId: string, params: { buyerId?: string; status?: string; shipFrom?: string; shipTo?: string; page?: number; size?: number }): Promise<OrderCostPage> =>
        (await axios.get(E.ORDERS(institutionId), { params })).data,
    getOrder: async (institutionId: string, orderId: string): Promise<OrderCostDetail> => (await axios.get(E.ORDER(institutionId, orderId))).data,
    pendingShipments: async (institutionId: string): Promise<PendingShipment[]> => (await axios.get(E.PENDING_SHIPMENTS(institutionId))).data,
    transferToFg: async (institutionId: string, orderId: string, body: EventBody): Promise<CostEvent> =>
        (await axios.post(E.TRANSFER_TO_FG(institutionId, orderId), body)).data,
    closeOrder: async (institutionId: string, orderId: string, body: EventBody): Promise<CostEvent> =>
        (await axios.post(E.CLOSE(institutionId, orderId), body)).data,
    costShipment: async (institutionId: string, invoiceId: string, body: EventBody): Promise<CostEvent> =>
        (await axios.post(E.COST_SHIPMENT(institutionId, invoiceId), body)).data,
    reverseEvent: async (institutionId: string, eventId: string): Promise<CostEvent> =>
        (await axios.post(E.REVERSE_EVENT(institutionId, eventId))).data,
};
